import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { Consumer } from '@/features/formNine/types';
import { FiltersGrid, Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { ConsumerInput, FormTen, FormTenGrid } from './types';

export const lastResortSuppliersApi = createApi({
  reducerPath: 'lastResortSuppliersApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/last-resort-suppliers`,
  }),
  tagTypes: [
    'FormTens',
    'FormTen',
    'FormTenValidRecords',
    'FormTenInvalidRecords',
  ],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getFormTensI: builder.infiniteQuery<
      FormTenGrid[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => {
        return {
          url: `/query`,
          params: { ...queryArg, ...pageParam },
        };
      },
      keepUnusedDataFor: 5,
      providesTags: () => ['FormTens', 'FormTen'],
    }),
    getFormTenById: builder.query<FormTen, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: result => [{ type: 'FormTen', id: result?.id }],
    }),
    getFormTenValidRecords: builder.infiniteQuery<
      Consumer[],
      FiltersGrid & { consumersInformationId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { consumersInformationId, ...args }, pageParam }) => {
        return {
          url: `/${consumersInformationId}/valid-records/query`,
          params: { ...args, ...pageParam },
        };
      },
      providesTags: () => ['FormTenValidRecords'],
    }),
    getFormTenInvalidRecords: builder.infiniteQuery<
      Consumer[],
      FiltersGrid & { consumersInformationId: string },
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { consumersInformationId, ...args }, pageParam }) => {
        return {
          url: `/${consumersInformationId}/invalid-records/query`,
          params: { ...args, ...pageParam },
        };
      },
      providesTags: () => ['FormTenInvalidRecords'],
    }),
    createFormTen: builder.mutation<string, FormData>({
      query: data => ({
        url: ``,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: id => [
        'FormTens',
        { type: 'FormTen', id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    updateFormTenById: builder.mutation<string, FormData>({
      query: data => ({
        url: `/${data.get('id')}`,
        method: 'PUT',
        headers: { 'Content-Type': 'multipart/form-data' },
        data,
      }),
      invalidatesTags: id => [
        'FormTens',
        { type: 'FormTen', id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    deleteFormTenById: builder.mutation<FormTen, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'FormTens',
        { type: 'FormTen', id: result?.id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    deleteAllErrorsFormTenById: builder.mutation<FormTen, string>({
      query: id => ({
        url: `/${id}/all-errors`,
        method: 'DELETE',
      }),
      invalidatesTags: result => [
        'FormTens',
        { type: 'FormTen', id: result?.id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    approveFormTen: builder.mutation<FormTen, string>({
      query: id => ({
        url: `/${id}/approve`,
        method: 'PUT',
      }),
      invalidatesTags: result => [
        'FormTens',
        { type: 'FormTen', id: result?.id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    updateFormTenRecords: builder.mutation<FormTen, ConsumerInput>({
      query: data => ({
        url: `/records`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        { type: 'FormTen', id: result?.id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
    deleteFormTenRecords: builder.mutation<FormTen, { records: string[] }>({
      query: ({ records }) => ({
        url: `/records`,
        method: 'DELETE',
        data: { records },
      }),
      invalidatesTags: result => [
        { type: 'FormTen', id: result?.id },
        'FormTenValidRecords',
        'FormTenInvalidRecords',
      ],
    }),
  }),
});

export const {
  useGetFormTenByIdQuery,
  useCreateFormTenMutation,
  useUpdateFormTenByIdMutation,
  useApproveFormTenMutation,
  useUpdateFormTenRecordsMutation,
  useDeleteFormTenRecordsMutation,
  useDeleteAllErrorsFormTenByIdMutation,
} = lastResortSuppliersApi;

export const useGetFormTensIInfiniteQuery = normalizeInfiniteQueryData(
  lastResortSuppliersApi.useGetFormTensIInfiniteQuery,
);

export const useGetFormTenValidRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    lastResortSuppliersApi.useGetFormTenValidRecordsInfiniteQuery,
  );

export const useGetFormTenInvalidRecordsInfiniteQuery =
  normalizeInfiniteQueryData(
    lastResortSuppliersApi.useGetFormTenInvalidRecordsInfiniteQuery,
  );

const getCachedFormTenByIdSelector = cachedItemByIdSelector(
  'lastResortSuppliersApi',
  'getFormTensI',
);

export const useGetCachedFormTenByIdSelector = id =>
  useSelector(getCachedFormTenByIdSelector(id));
